/**
 * @file 游戏列表单行（Layer 1 纯 UI）。
 * 由 `ChartGameSelect` 在分组列表中渲染，负责主版本行与子版本 Popover。
 */
import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Popover } from 'tea-component';
import type { GameInfoType } from '@/pages/dashboard/components/Navigation/GameSelector/types';
import GameInfo from './GameInfo';
import type { IGameRowProps } from '../types';

/**
 * 获取可展示的子版本列表。
 *
 * 单子版本且与主版本 `game_code` 相同时过滤，避免重复展示。
 *
 * @param gameInfo - 主版本游戏数据
 * @returns 可用于二级面板的子版本列表
 */
const getChildGameList = (gameInfo: GameInfoType): GameInfoType[] => {
  const gameList = gameInfo?.game_List;
  if (!gameList?.length) return [];
  return gameList.length === 1
    ? gameList.filter((item) => item?.game_code !== gameInfo?.game_code)
    : gameList;
};

/**
 * 游戏列表单行。
 *
 * @param props - 行属性，见 {@link IGameRowProps}
 *
 * @remarks
 * - 无子版本：点击直接选中主版本 `game_code`
 * - 有子版本：hover 右侧 Popover 展示子版本，点击叶子项选中
 * - 子版本已选中时，主版本行同步展示选中态
 * - 二级面板宽度与一级 `listWidth` 保持一致
 */
const GameRow: React.FC<IGameRowProps> = ({
  gameInfo,
  searchValue = '',
  selectedGameCode,
  onSelectGame,
  listWidth = 400,
}) => {
  const [visible, setVisible] = useState(false);

  const childGameList = useMemo(() => getChildGameList(gameInfo), [gameInfo]);
  const accessGameList = useMemo(
    () => childGameList.filter((item) => item.is_access),
    [childGameList],
  );
  const hasSubGames = accessGameList.length > 0;

  const handleSelect = (gameCode?: string): void => {
    if (!gameCode) return;
    onSelectGame(gameCode);
    setVisible(false);
  };

  const renderSubRow = (item: GameInfoType): React.ReactNode => {
    const isSelected = item.game_code === selectedGameCode;
    return (
      <div
        key={item.game_code}
        className={classNames('dba-new-chart-game-row', {
          'dba-new-chart-game-row--selected': isSelected,
        })}
        onClick={() => handleSelect(item.game_code)}
      >
        <GameInfo
          gameInfo={{
            ...item,
            cover: item.cover,
          }}
        />
      </div>
    );
  };

  const hostSelected = !hasSubGames
    && gameInfo.is_access
    && selectedGameCode === gameInfo.game_code;
  const childSelected = hasSubGames
    && accessGameList.some((item) => item.game_code === selectedGameCode);

  const hostRow = (
    <div
      className={classNames('dba-new-chart-game-row', {
        'dba-new-chart-game-row--hover': visible && hasSubGames,
        'dba-new-chart-game-row--selected': hostSelected || childSelected,
      })}
      onClick={() => {
        if (hasSubGames || !gameInfo.is_access) return;
        handleSelect(gameInfo.game_code);
      }}
    >
      <GameInfo
        gameInfo={gameInfo}
        isMajorVersion
        searchValue={searchValue}
        showArrow={hasSubGames}
      />
    </div>
  );

  if (!hasSubGames) {
    return hostRow;
  }

  return (
    <Popover
      visible={visible}
      onVisibleChange={setVisible}
      placement="right-start"
      trigger="hover"
      positionFixed={false}
      updateOnDimensionChange
      overlayClassName="dba-new-chart-game-sub-overlay"
      overlay={(
        <div
          className="dba-new-chart-game-sub-panel"
          style={{ width: listWidth }}
        >
          {accessGameList.map((item) => renderSubRow(item))}
        </div>
      )}
    >
      {hostRow}
    </Popover>
  );
};

export default GameRow;
